import React from 'react';
import { Check, X } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { classNames } from '@/utils/classNames.utils';

const MIN_PASSWORD_LENGTH = 12;

export interface PasswordRequirement {
  id: 'length' | 'number' | 'uppercase' | 'lowercase' | 'special' | 'match';
  label: string;
  test: (pwd: string, confirmPwd?: string) => boolean;
}

const DEFAULT_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (pwd) => pwd.length >= MIN_PASSWORD_LENGTH,
  },
  { id: 'number', label: 'At least 1 number', test: (pwd) => /[0-9]/.test(pwd) },
  { id: 'uppercase', label: 'At least 1 uppercase', test: (pwd) => /[A-Z]/.test(pwd) },
  { id: 'lowercase', label: 'At least 1 lowercase', test: (pwd) => /[a-z]/.test(pwd) },
  { id: 'special', label: 'At least 1 special character', test: (pwd) => /[!@#$%^&*()_+\-=[\]{}|;:'",.<>/?`~]/.test(pwd) },
];

const requirementRowVariants = cva(
  'flex items-center gap-2 transition-all duration-300 ease-in-out text-sm font-normal leading-normal',
  {
    variants: {
      met: {
        true: 'text-green-600',
        false: 'text-muted-foreground',
      },
    },
    defaultVariants: {
      met: false,
    },
  }
);

const iconWrapperVariants = cva(
  'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-300',
  {
    variants: {
      met: {
        true: 'bg-primary/10',
        false: 'bg-muted',
      },
    },
    defaultVariants: {
      met: false,
    },
  }
);

interface PasswordRequirementsProps extends React.HTMLAttributes<HTMLDivElement> {
  password: string;
  confirmPassword?: string;
  requirements?: PasswordRequirement[];
  heading?: string;
  shouldHideHeading?: boolean; // Renamed from hideHeading
  includeMatchRequirement?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  confirmPassword,
  requirements = DEFAULT_REQUIREMENTS,
  heading = 'Password Requirements',
  shouldHideHeading = false,
  includeMatchRequirement = false,
  className,
  containerRef,
  ...rest
}) => {
  const displayedRequirements = React.useMemo<PasswordRequirement[]>(() => {
    if (!includeMatchRequirement) {
      return requirements;
    }

    return [
      ...requirements,
      {
        id: 'match',
        label: 'Passwords match',
        test: (pwd: string, confirmPwd?: string) =>
          typeof confirmPwd === 'string' && confirmPwd.length > 0 && pwd === confirmPwd,
      },
    ];
  }, [includeMatchRequirement, requirements]);

  return (
    <div
      ref={containerRef}
      className={classNames('w-full', className)}
      aria-live="polite"
      {...rest}
    >
      {!shouldHideHeading && (
        <h4 className="mb-3 text-sm font-semibold text-foreground">
          {heading}
        </h4>
      )}

      <div className="flex flex-col gap-2">
        {displayedRequirements.map((req) => {
          const isMet = req.test(password, confirmPassword);

          return (
            <div
              key={req.id}
              className={requirementRowVariants({ met: isMet })}
            >
              <div
                className={iconWrapperVariants({ met: isMet })}
                aria-hidden="true"
              >
                {isMet ? (
                  <div className="flex h-4 w-4 items-center justify-center rounded-full bg-green-500">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </div>
                ) : (
                  <X className="h-3 w-3 text-muted-foreground" strokeWidth={2.5} />
                )}
              </div>

              <span className="flex-1 break-words">{req.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PasswordRequirements;
